import * as React from "react";

interface SeoProps {
  title: string;
  description: string;
  image?: string;
  canonical?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const setTag = (attr: "name" | "property", key: string, content: string) => {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href: string) => {
  let link = document.head.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

const setJsonLd = (data: Record<string, unknown> | Record<string, unknown>[]) => {
  const id = "seo-jsonld";
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(data);
};

export const Seo: React.FC<SeoProps> = ({ title, description, image, canonical, ogType = "website", jsonLd }) => {
  React.useEffect(() => {
    const url = canonical || window.location.href;
    document.title = title;
    setTag("name", "description", description);

    // OpenGraph
    setTag("property", "og:title", title);
    setTag("property", "og:description", description);
    setTag("property", "og:type", ogType);
    setTag("property", "og:url", url);
    if (image) setTag("property", "og:image", image);

    // Twitter
    setTag("name", "twitter:card", image ? "summary_large_image" : "summary");
    setTag("name", "twitter:title", title);
    setTag("name", "twitter:description", description);
    if (image) setTag("name", "twitter:image", image);

    // Canonical
    setCanonical(url);

    // JSON-LD
    if (jsonLd) setJsonLd(jsonLd);
  }, [title, description, image, canonical, ogType, jsonLd]);

  return null;
};
